import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ChildrenOutletContexts, NavigationEnd, Router } from '@angular/router';
import { HTTP_CP_ClientUser } from '@api-new/userservice';
import { environment } from '@environments/environment';
import { Store, select } from '@ngrx/store';
import { selectWelcomeModalData } from '@platform/dashboard/components/welcome-modal/welcome-modal.selector';
import { slideInAnimation } from '@platform/route-animations';
import {
  ScreenSizeBreakpointNames,
  ScreenSizeBreakpointObserverService,
} from '@shared-lib/services/screen-size-breakpoint-observer.service';
import { slideFromBottom } from '@shared/animations/slideFromBottom';
import { PAGES_WITHOUT_MOBILE_HEADER, PAGES_WITH_MOBILE_HEADER_AND_MENU_VISIBLE } from '@shared/contants';
import { AppState } from '@shared/models/app-state.model';
import { AnalyticsService } from '@shared/services/analytics.service';
import { AuthService } from '@shared/services/auth.service';
import { NotificationsService } from '@shared/services/notifications.service';
import { getAdviserDetail } from '@shared/store/adviser/adviser.actions';
import { getClientUser, updateClientUserSilently } from '@shared/store/client-user/client-user.actions';
import { selectClientUser } from '@shared/store/client-user/client-user.selectors';
import { loadInitialPlatformData } from '@shared/store/common.actions';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Component({
  selector: 'cp-platform',
  templateUrl: './platform.component.html',
  styleUrls: ['platform.component.scss'],
  animations: [slideInAnimation, slideFromBottom],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class PlatformComponent implements OnInit {
  protected readonly welcomeModalData$ = this.store.pipe(select(selectWelcomeModalData));
  protected readonly size$: Observable<ScreenSizeBreakpointNames> = this.screenSizeBreakpointObserverService.screenSizeBreakpointName$;
  protected readonly isMobileApp = environment.mobileApp?.isMobileApp;

  @ViewChild('platformContent', { static: true }) platformContent: ElementRef;

  protected user: HTTP_CP_ClientUser;
  protected shouldDisplayMobileHeaderAndMenu = false;

  // This is for slideInAnimation to know from where to trigger
  private lastMousePosition: MouseEvent;
  protected readonly ScreenSizeBreakpointNames = ScreenSizeBreakpointNames;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store<AppState>,
    private readonly screenSizeBreakpointObserverService: ScreenSizeBreakpointObserverService,
    public readonly authService: AuthService,
    private readonly contexts: ChildrenOutletContexts,
    private readonly notificationsService: NotificationsService,
    private readonly analytics: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(loadInitialPlatformData());

    this.store.dispatch(getClientUser());

    this.store.pipe(select(selectClientUser), filter(Boolean), take(1)).subscribe((user) => {
      this.user = user;
      if (user?.advisorUserId) this.store.dispatch(getAdviserDetail());
      this.notificationsService.initPushNotifications(user?.id);
      this.analytics.setAppOpened(user.id);
      this.analytics.setLoggedIn(true);
    });

    this.setShouldDisplayHeaderAndMenu();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setShouldDisplayHeaderAndMenu();
      }
    });
  }

  setShouldDisplayHeaderAndMenu = (): void => {
    this.shouldDisplayMobileHeaderAndMenu =
      PAGES_WITH_MOBILE_HEADER_AND_MENU_VISIBLE.some((path) => this.router.url.includes(path)) &&
      !PAGES_WITHOUT_MOBILE_HEADER.some((path) => this.router.url.includes(path));
  };

  onActivate(): void {
    if (this.platformContent) {
      (this.platformContent.nativeElement as HTMLElement).scrollTop = 0; // Scroll to top on redirect
    }
  }

  getRouteAnimationData(): Observable<{ value: unknown; params: unknown }> {
    return this.route.paramMap.pipe(
      map(() => ({
        value: this.contexts.getContext('primary')?.route?.snapshot?.data?.['animation'],
        params: { ...window.history.state, top: this.lastMousePosition?.pageY, left: this.lastMousePosition?.pageX },
      })),
    );
  }

  cancelTutorials(): void {
    this.user.tutorialCleared = true;
    this.store.dispatch(updateClientUserSilently({ clientUser: this.user }));
  }

  setLastMousePosition($event: MouseEvent): void {
    this.lastMousePosition = $event;
  }
}
